<template>
    <div class="relative" v-click-outside="onClickOutside">
        <span class="cursor-pointer" v-on:click="toggleMenu">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 14 14"
                class="w-5 h-5 stroke-current text-gray-500 hover:text-navy-dark"
                :class="{
                    'text-navy-dark': menuopen,
                    'text-gray-500': !menuopen,
                }"
            >
                <g>
                    <circle
                        cx="7"
                        cy="2"
                        r="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></circle>
                    <circle
                        cx="7"
                        cy="7"
                        r="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></circle>
                    <circle
                        cx="7"
                        cy="12"
                        r="1.5"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    ></circle>
                </g>
            </svg>
        </span>
        <div v-if="menuopen" class="absolute w-64 top-4 right-0 z-50 bg-white rounded-lg shadow-lg border border-gray-200">
            <!-- Tab Headers -->
            <div class="flex border-b border-gray-200">
                <button 
                    @click="activeTab = 'soil'"
                    class="flex-1 px-3 py-3 text-sm font-medium rounded-tl-lg flex items-center justify-center space-x-1"
                    :class="activeTab === 'soil' 
                        ? 'bg-green-50 text-green-700 border-b-2 border-green-600' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                >
                    <svg-icon type="mdi" :path="icons.soilAnalysisReports" :size="16"></svg-icon>
                    <span>Soil Analysis</span>
                    <div class="relative">
                        <button
                            @click.stop="showSoilAnalysisInfo = !showSoilAnalysisInfo"
                            class="p-1 text-green-500 hover:text-green-700 focus:outline-none transition-colors"
                            type="button"
                            title="View soil analysis report information"
                        >
                            <svg-icon type="mdi" :path="icons.info" :size="12"></svg-icon>
                        </button>
                        
                        <!-- Info Popover -->
                        <div
                            v-if="showSoilAnalysisInfo"
                            class="absolute top-6 right-0 w-64 bg-white border border-green-200 rounded-lg shadow-lg z-50 p-3"
                            @click.stop
                        >
                            <div class="text-xs">
                                <p class="font-medium text-green-700 mb-1">Soil Analysis Reports</p>
                                <p class="text-green-600">Full report with color-coded maps based on soil test results</p>
                            </div>
                            <div class="absolute top-0 right-4 transform -translate-y-1">
                                <div class="w-2 h-2 bg-white border-l border-t border-green-200 transform rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </button>
                <button 
                    @click="activeTab = 'task'"
                    class="flex-1 px-3 py-3 text-sm font-medium rounded-tr-lg flex items-center justify-center space-x-1"
                    :class="activeTab === 'task' 
                        ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                >
                    <svg-icon type="mdi" :path="icons.taskReports" :size="16"></svg-icon>
                    <span>Task Reports</span>
                    <div class="relative">
                        <button
                            @click.stop="showTaskReportInfo = !showTaskReportInfo"
                            class="p-1 text-blue-500 hover:text-blue-700 focus:outline-none transition-colors"
                            type="button"
                            title="View task report information"
                        >
                            <svg-icon type="mdi" :path="icons.info" :size="12"></svg-icon>
                        </button>
                        
                        <!-- Info Popover -->
                        <div
                            v-if="showTaskReportInfo"
                            class="absolute top-6 right-0 w-64 bg-white border border-blue-200 rounded-lg shadow-lg z-50 p-3"
                            @click.stop
                        >
                            <div class="text-xs">
                                <p class="font-medium text-blue-700 mb-1">Task Reports</p>
                                <p class="text-blue-600">Basic report consisting of only the soil sampling map</p>
                            </div>
                            <div class="absolute top-0 right-4 transform -translate-y-1">
                                <div class="w-2 h-2 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </button>
            </div>

            <!-- Tab Content -->
            <div class="p-3">
                <!-- Soil Analysis Reports Tab -->
                <div v-if="activeTab === 'soil'">
                    <!-- No Lab Results Message -->
                    <div v-if="isSoilAnalysisDisabled" class="py-6 text-center text-gray-600">
                        <div class="flex flex-col items-center space-y-3">
                            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
                            </svg>
                            <div>
                                <p class="font-medium text-gray-700 text-base">No Lab Results Available</p>
                                <p class="text-sm text-gray-500 mt-1 max-w-64">
                                    Soil analysis reports require at least one sample with lab test results.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Active State - Normal Actions -->
                    <div v-else-if="viewSoilAnalysisReport && createLink" class="space-y-1">
                        <div 
                            v-if="viewSoilAnalysisReport && createLink"
                            class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-green-50 transition-colors">
                            <a
                                class="flex items-center space-x-3 cursor-pointer"
                                @click="handleCreateSoilAnalysisReport"
                            >
                                <svg-icon type="mdi" :path="icons.createReport" :size="16" class="text-green-600"></svg-icon>
                                <span v-if="pdfCreated">Re-create</span>
                                <span v-else>Create</span>
                            </a>
                        </div>

                        <div
                            v-if="viewSoilAnalysisReport && pdfCreated"
                            class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-green-50 transition-colors"
                        >
                            <a
                                class="flex items-center space-x-3 cursor-pointer"
                                @click.stop="showPreviewModal('soil-analysis')"
                            >
                                <svg-icon type="mdi" :path="icons.viewReport" :size="16" class="text-green-600"></svg-icon>
                                <span>View</span>
                            </a>
                        </div>

                        <div
                            v-if="viewSoilAnalysisReport && pdfCreated && canEmail"
                            class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-green-50 transition-colors"
                        >
                            <a class="flex items-center justify-between cursor-pointer" @click.stop="showEmailModal('soil-analysis')">
                                <div class="flex items-center space-x-3">
                                    <svg-icon type="mdi" :path="icons.emailReport" :size="16" class="text-green-600"></svg-icon>
                                    <span>Email</span>
                                </div>
                                <svg v-if="soilAnalysisEmailedAt" class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                </svg>
                            </a>
                        </div>
                    </div>
                    
                    <!-- Empty State for when no soil analysis actions are available -->
                    <div v-else class="py-6 text-center text-gray-500 text-sm">
                        No soil analysis report actions available
                    </div>
                </div>

                <!-- Task Reports Tab -->
                <div v-if="activeTab === 'task' && (createTaskReport || taskReportCreated)" class="space-y-1">
                    <div 
                        v-if="createTaskReport"
                        class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors">
                        <a
                            class="flex items-center space-x-3 cursor-pointer"
                            @click="handleCreateTaskReport"
                        >
                            <svg-icon type="mdi" :path="icons.createReport" :size="16" class="text-blue-600"></svg-icon>
                            <span v-if="taskReportCreated">Re-create</span>
                            <span v-else>Create</span>
                        </a>
                    </div>

                    <div
                        v-if="taskReportCreated"
                        class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors"
                    >
                        <a
                            class="flex items-center space-x-3 cursor-pointer"
                            @click.stop="showPreviewModal('task_report')"
                        >
                            <svg-icon type="mdi" :path="icons.viewReport" :size="16" class="text-blue-600"></svg-icon>
                            <span>View</span>
                        </a>
                    </div>

                    <div
                        v-if="taskReportCreated && canEmail"
                        class="py-2 px-3 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors"
                    >
                        <a class="flex items-center justify-between cursor-pointer" @click.stop="showEmailModal('task_report')">
                            <div class="flex items-center space-x-3">
                                <svg-icon type="mdi" :path="icons.emailReport" :size="16" class="text-blue-600"></svg-icon>
                                <span>Email</span>
                            </div>
                            <svg v-if="taskReportEmailedAt" class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                            </svg>
                        </a>
                    </div>
                </div>

                <!-- Empty State Messages -->
                <div v-if="activeTab === 'task' && !createTaskReport && !taskReportCreated" class="py-6 text-center text-gray-500 text-sm">
                    No task report actions available
                </div>
            </div>
        </div>

        <!-- Report Preview Modal -->
        <ReportPreviewModal
            :isOpen="previewModalOpen"
            :modalTitle="'Report Preview'"
            :reportType="previewReportType === 'soil-analysis' ? 'soil_analysis' : previewReportType"
            :pdfUrl="previewPdfUrl"
            :downloadUrl="previewDownloadUrl"
            :showDownload="true"
            :showEmail="canEmail"
            :showFtpUpload="canUploadToDairygold && isDairygoldTask"
            :noPreviewMessage="'Report preview not available'"
            :noPreviewSubMessage="'Generate the report first to see preview'"
            @close="closePreviewModal"
            @email-report="showEmailFromPreview"
            @ftp-upload="uploadReportFromPreview"
        />

        <!-- Email Modal Using Shared Component -->
        <ReportPreviewModal
            :isOpen="emailModalOpen"
            :modalTitle="'Email Report'"
            :reportType="currentReportType === 'soil-analysis' ? 'soil_analysis' : currentReportType"
            :pdfUrl="pdfUrl"
            :showDownload="false"
            :showEmail="false"
            :showFtpUpload="false"
            :noPreviewMessage="'Report preview not available'"
            :noPreviewSubMessage="'Generate the report first to see preview'"
            @close="closeEmailModal"
        >
            <template #leftPanel>
                <form @submit.prevent="sendEmail" class="h-full flex flex-col">
                    <div class="flex-1 space-y-4">
                        <h4 class="font-medium text-gray-800 mb-4">Send Email</h4>
                        
                        <div>
                            <label for="emails" class="block text-sm font-medium text-gray-700 mb-1">
                                Email Addresses * <span class="text-xs text-gray-500">(one per line or comma-separated)</span>
                            </label>
                            <textarea 
                                id="emails"
                                v-model="emailForm.emails"
                                rows="3"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter email addresses..."
                                required
                            ></textarea>
                        </div>

                        <div>
                            <label for="cc_email" class="block text-sm font-medium text-gray-700 mb-1">
                                CC Email (optional)
                            </label>
                            <input 
                                type="email" 
                                id="cc_email"
                                v-model="emailForm.cc_email"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label for="message" class="block text-sm font-medium text-gray-700 mb-1">
                                Custom Message (optional)
                            </label>
                            <textarea 
                                id="message"
                                v-model="emailForm.message"
                                rows="4"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                maxlength="1000"
                            ></textarea>
                        </div>

                        <!-- Lab Certificate Option -->
                        <div v-if="hasLabCertificateAvailable">
                            <label class="flex items-center">
                                <input 
                                    type="checkbox" 
                                    v-model="emailForm.include_lab_certificate"
                                    class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-offset-0 focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span class="ml-2 text-sm font-medium text-gray-700">
                                    Include Lab Certificate (FBA Labs)
                                </span>
                            </label>
                            <p class="mt-1 text-xs text-gray-500">
                                Lab certificate will be attached to the email if available
                            </p>
                        </div>
                    </div>

                    <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                        <button 
                            type="button"
                            @click="closeEmailModal"
                            class="px-4 py-2 text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit"
                            :disabled="emailSending"
                            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {{ emailSending ? 'Sending...' : 'Send Email' }}
                        </button>
                    </div>
                </form>
            </template>
        </ReportPreviewModal>
    </div>
</template>

<script>
import vClickOutside from "click-outside-vue3";
import { useToast } from "vue-toastification";
import axios from "axios";
import SvgIcon from '@jamescoyle/vue-icon';
import * as icons from '../Partials/icons.js';
import ReportPreviewModal from './ReportPreviewModal.vue';

export default {
    components: {
        SvgIcon,
        ReportPreviewModal,
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    emits: ['ftp-upload-success', 'report-generation-started'],
    props: {
        viewLink: String,
        createTaskReport: String,
        viewTaskReport: String,
        pdfCreated: Boolean,
        taskReportCreated: Boolean,
        viewSoilAnalysisReport: Boolean,
        createLink: String,
        warning: Object,
        canEmail: Boolean,
        emailReportRoute: String,
        taskId: String,
        farmId: String,
        taskReportEmailedAt: String,
        soilAnalysisEmailedAt: String,
        canUploadToDairygold: Boolean,
        isDairygoldTask: Boolean,
        availableLabCertificates: Array,
    },
    data() {
        return {
            menuopen: false,
            activeTab: 'soil',
            showTaskReportInfo: false,
            showSoilAnalysisInfo: false,
            emailModalOpen: false,
            emailSending: false,
            currentReportType: null,
            previewModalOpen: false,
            previewReportType: null,
            emailForm: {
                emails: '',
                cc_email: '',
                message: '',
                include_lab_certificate: true
            },
            icons
        };
    },
    mounted() {
        this.$toast = useToast();
    },
    watch: {
        previewModalOpen(newVal, oldVal) {
            // Modal state changed
        },
        previewReportType(newVal, oldVal) {
            // Report type changed
        }
    },
    computed: {
        hasLabCertificateAvailable() {
            if (!this.availableLabCertificates || this.availableLabCertificates.length === 0) {
                return false;
            }
            
            return this.availableLabCertificates.some(cert => {
                return cert.test_id && this.farmId;
            });
        },
        
        isSoilAnalysisDisabled() {
            return this.warning && this.warning.hasTestsWithNoLabResult;
        },

        pdfUrl() {
            if (!this.currentReportType || !this.taskId || !this.farmId) {
                return null;
            }

            const isReportGenerated = this.currentReportType === 'soil-analysis' 
                ? this.pdfCreated 
                : this.taskReportCreated;

            if (!isReportGenerated) {
                return null;
            }

            try {
                const filename = this.currentReportType === 'soil-analysis' 
                    ? 'Soil Analysis Report.pdf' 
                    : 'Task Report.pdf';
                    
                const baseUrl = route('app.task.view-pdf', {
                    task: this.taskId,
                    farm: this.farmId,
                    report_type: this.currentReportType,
                    filename: filename
                });
                
                return `${baseUrl}?inline=1`;
            } catch (error) {
                console.error("Error constructing PDF URL:", error);
                return null;
            }
        },

        previewPdfUrl() {
            if (!this.previewReportType || !this.taskId || !this.farmId) {
                return null;
            }

            const isReportGenerated = this.previewReportType === 'soil-analysis' 
                ? this.pdfCreated 
                : this.taskReportCreated;

            if (!isReportGenerated) {
                return null;
            }

            try {
                const filename = this.previewReportType === 'soil-analysis' 
                    ? 'Soil Analysis Report.pdf' 
                    : 'Task Report.pdf';
                    
                const baseUrl = route('app.task.view-pdf', {
                    task: this.taskId,
                    farm: this.farmId,
                    report_type: this.previewReportType,
                    filename: filename
                });
                
                return `${baseUrl}?inline=1`;
            } catch (error) {
                console.error("Error constructing PDF URL:", error);
                return null;
            }
        },

        previewDownloadUrl() {
            if (!this.previewReportType || !this.taskId || !this.farmId) {
                return null;
            }
            
            const isReportGenerated = this.previewReportType === 'soil-analysis' 
                ? this.pdfCreated 
                : this.taskReportCreated;

            if (!isReportGenerated) {
                return null;
            }

            try {
                const filename = this.previewReportType === 'soil-analysis' 
                    ? 'Soil Analysis Report.pdf' 
                    : 'Task Report.pdf';
                    
                return route('app.task.view-pdf', {
                    task: this.taskId,
                    farm: this.farmId,
                    report_type: this.previewReportType,
                    filename: filename
                });
            } catch (error) {
                console.error("Error constructing download URL:", error);
                return null;
            }
        }
    },
    methods: {
        toggleMenu() {
            this.menuopen = !this.menuopen;
            // Always reset to default 'soil' tab when opening the menu
            if (this.menuopen) {
                this.activeTab = 'soil';
            }
        },

        onClickOutside(event) {
            this.menuopen = false;
            this.showTaskReportInfo = false;
            this.showSoilAnalysisInfo = false;
        },

        showPreviewModal(reportType) {
            this.previewReportType = reportType;
            
            // Use nextTick to ensure proper timing
            this.$nextTick(() => {
                this.previewModalOpen = true;
                // Close dropdown menu after modal opens
                this.menuopen = false;
            });
        },

        closePreviewModal() {
            this.previewModalOpen = false;
            this.previewReportType = null;
        },

        showEmailFromPreview() {
            this.showEmailModal(this.previewReportType);
            this.closePreviewModal();
        },

        uploadReportFromPreview() {
            this.uploadToDairygold(this.previewReportType);
            this.closePreviewModal();
        },

        showEmailModal(reportType) {
            this.currentReportType = reportType;
            this.emailModalOpen = true;
            this.menuopen = false;
            
            // Fetch email suggestions
            this.fetchEmailSuggestions();
        },

        async fetchEmailSuggestions() {
            try {
                const url = route('app.task.email-suggestions', {
                    task: this.taskId
                });
                
                const response = await axios.get(url);
                
                if (response.data.success) {
                    const suggestions = response.data.suggestions;
                    
                    // Pre-populate all relevant emails
                    if (suggestions.to_emails && suggestions.to_emails.length > 0) {
                        this.emailForm.emails = suggestions.to_emails.join('\n');
                    }
                    
                    // Set sender email as CC
                    if (suggestions.sender_email) {
                        this.emailForm.cc_email = suggestions.sender_email;
                    }
                }
            } catch (error) {
                console.warn('Failed to fetch email suggestions:', error);
                // Continue with modal even if suggestions fail
            }
        },

        closeEmailModal() {
            this.emailModalOpen = false;
            this.emailForm.emails = '';
            this.emailForm.cc_email = '';
            this.emailForm.message = '';
            this.currentReportType = null;
        },

        async sendEmail() {
            if (!this.emailForm.emails.trim()) {
                this.$toast.error('Please enter at least one email address');
                return;
            }

            // Parse emails from textarea (support both newlines and commas)
            const emails = this.emailForm.emails
                .split(/[\n,]+/)
                .map(email => email.trim())
                .filter(email => email.length > 0);

            if (emails.length === 0) {
                this.$toast.error('Please enter valid email addresses');
                return;
            }

            this.emailSending = true;
            try {
                const response = await axios.post(this.emailReportRoute, {
                    emails: emails,
                    cc_email: this.emailForm.cc_email,
                    message: this.emailForm.message,
                    include_lab_certificate: this.emailForm.include_lab_certificate,
                    report_type: this.currentReportType,
                });
                this.$toast.success(response.data.message || "Email sent successfully!");
                this.closeEmailModal();
            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Failed to send email.");
            } finally {
                this.emailSending = false;
            }
        },

        uploadToDairygold(reportType) {
            this.$emit('report-generation-started', {
                farmId: this.farmId,
                reportType: reportType
            });
            axios.post(route('dairygold.upload'), {
                task_id: this.taskId,
                report_type: reportType,
            })
            .then(response => {
                this.$toast.success(response.data.message);
                this.$emit('ftp-upload-success');
            })
            .catch(error => {
                this.$toast.error(error.response.data.message);
            })
            .finally(() => {
                this.menuopen = false;
            });
        },

        handleCreateSoilAnalysisReport() {
            this.$emit('report-generation-started', { farmId: this.farmId, reportType: 'soil_analysis' });
            this.$inertia.post(this.createLink);
            this.$toast.success("Soil analysis report is being generated");
        },

        handleCreateTaskReport() {
            this.$emit('report-generation-started', { farmId: this.farmId, reportType: 'task_report' });
            this.$inertia.post(this.createTaskReport);
            this.$toast.success("Task report is being generated");
        },
    },
};
</script>
